import { useState, useEffect, useRef, type RefObject } from 'react';
import { paginationBatchSize } from './Constants';
import { ErrorType } from './Types';

export const useResponsiveElementWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const handleResize = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
        setHeight(entry.contentRect.height);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref]);

  return [ref, width, height] as const;
};

export const useInboxListScrollPagination = (
  listContainerElement: HTMLElement | null,
  hasNext: boolean,
  isLoadingNext: boolean,
  loadNext: (batchSize: number) => void,
  autoLoadKey = '',
  hasDeferredPage = false
) => {
  const autoLoadStateRef = useRef<{
    element: HTMLElement | null;
    key: string;
    lastAutoLoadHeight: number | null;
  }>({
    element: null,
    key: '',
    lastAutoLoadHeight: null,
  });
  const deferredPageKeyRef = useRef(autoLoadKey);
  const didLoadDeferredPageRef = useRef(false);

  if (deferredPageKeyRef.current !== autoLoadKey) {
    deferredPageKeyRef.current = autoLoadKey;
    didLoadDeferredPageRef.current = false;
  }

  if (
    autoLoadStateRef.current.element !== listContainerElement ||
    autoLoadStateRef.current.key !== autoLoadKey
  ) {
    autoLoadStateRef.current = {
      element: listContainerElement,
      key: autoLoadKey,
      lastAutoLoadHeight: null,
    };
  }

  useEffect(() => {
    const loadNextIfAvailable = () => {
      if (!hasNext || isLoadingNext) {
        return false;
      }

      loadNext(paginationBatchSize);
      return true;
    };

    const handleScrollPagination = () => {
      if (!listContainerElement || !hasNext || isLoadingNext) {
        return;
      }

      const isScrolledToBottom =
        listContainerElement.clientHeight > 0 &&
        listContainerElement.scrollHeight -
          listContainerElement.scrollTop -
          10 <=
          listContainerElement.clientHeight;
      if (isScrolledToBottom) {
        loadNextIfAvailable();
      }
    };

    if (
      hasDeferredPage &&
      listContainerElement &&
      !isLoadingNext &&
      !didLoadDeferredPageRef.current
    ) {
      didLoadDeferredPageRef.current = true;
      loadNext(paginationBatchSize);
      return;
    }

    if (hasNext && listContainerElement) {
      const listDoesNotOverflow =
        listContainerElement.clientHeight > 0 &&
        listContainerElement.scrollHeight - listContainerElement.scrollTop <=
          listContainerElement.clientHeight;

      if (listDoesNotOverflow) {
        const currentHeight = listContainerElement.scrollHeight;
        if (autoLoadStateRef.current.lastAutoLoadHeight !== currentHeight) {
          if (loadNextIfAvailable()) {
            autoLoadStateRef.current.lastAutoLoadHeight = currentHeight;
          }
        }
      } else {
        listContainerElement.addEventListener('scroll', handleScrollPagination);
        listContainerElement.addEventListener('resize', handleScrollPagination);
        handleScrollPagination();
      }
    }
    return () => {
      if (listContainerElement) {
        listContainerElement.removeEventListener(
          'scroll',
          handleScrollPagination
        );
        listContainerElement.removeEventListener(
          'resize',
          handleScrollPagination
        );
      }
    };
  }, [
    listContainerElement,
    hasNext,
    isLoadingNext,
    loadNext,
    autoLoadKey,
    hasDeferredPage,
  ]);
};

export const useInboxListTopScrollPagination = (
  listContainerElement: HTMLElement | null,
  hasPrevious: boolean,
  isLoadingPrevious: boolean,
  loadPrevious: (batchSize: number) => void,
  autoLoadKey = '',
  batchSize = paginationBatchSize
) => {
  const paginationStateRef = useRef<{
    element: HTMLElement | null;
    key: string;
    lastAutoLoadHeight: number | null;
    didLoadAtTopWithoutLeaving: boolean;
  }>({
    element: null,
    key: '',
    lastAutoLoadHeight: null,
    didLoadAtTopWithoutLeaving: false,
  });

  if (
    paginationStateRef.current.element !== listContainerElement ||
    paginationStateRef.current.key !== autoLoadKey
  ) {
    paginationStateRef.current = {
      element: listContainerElement,
      key: autoLoadKey,
      lastAutoLoadHeight: null,
      didLoadAtTopWithoutLeaving: false,
    };
  }

  useEffect(() => {
    const loadPreviousIfAvailable = () => {
      if (!hasPrevious || isLoadingPrevious) {
        return false;
      }

      loadPrevious(batchSize);
      return true;
    };

    const handleScrollPagination = () => {
      if (!listContainerElement || !hasPrevious || isLoadingPrevious) {
        return;
      }

      const isScrolledToTop = listContainerElement.scrollTop <= 10;
      if (!isScrolledToTop) {
        paginationStateRef.current.didLoadAtTopWithoutLeaving = false;
        return;
      }

      if (paginationStateRef.current.didLoadAtTopWithoutLeaving) {
        return;
      }

      if (loadPreviousIfAvailable()) {
        paginationStateRef.current.didLoadAtTopWithoutLeaving = true;
      }
    };

    if (hasPrevious && listContainerElement) {
      const listDoesNotOverflow =
        listContainerElement.clientHeight > 0 &&
        listContainerElement.scrollHeight <= listContainerElement.clientHeight;

      if (listDoesNotOverflow) {
        const currentHeight = listContainerElement.scrollHeight;
        if (paginationStateRef.current.lastAutoLoadHeight !== currentHeight) {
          if (loadPreviousIfAvailable()) {
            paginationStateRef.current.lastAutoLoadHeight = currentHeight;
          }
        }
      } else {
        listContainerElement.addEventListener('scroll', handleScrollPagination);
        listContainerElement.addEventListener('resize', handleScrollPagination);
      }
    }

    return () => {
      if (listContainerElement) {
        listContainerElement.removeEventListener(
          'scroll',
          handleScrollPagination
        );
        listContainerElement.removeEventListener(
          'resize',
          handleScrollPagination
        );
      }
    };
  }, [
    listContainerElement,
    hasPrevious,
    isLoadingPrevious,
    loadPrevious,
    autoLoadKey,
    batchSize,
  ]);
};

export const useTenantScrollPagination = <RelayDataType>(
  data: RelayDataType,
  hasNext: boolean,
  loadNext: (batchSize: number) => void,
  isLoadingNext: boolean
) => {
  useEffect(() => {
    if (!hasNext) {
      return;
    }

    const scrollingElement =
      document.scrollingElement || document.documentElement;

    const getScrollPosition = () =>
      (scrollingElement?.scrollTop || 0) + window.innerHeight;

    const getScrollHeight = () =>
      scrollingElement?.scrollHeight || document.documentElement.scrollHeight;

    const handleScrollPagination = () => {
      if (!hasNext || isLoadingNext) {
        return;
      }

      const nearBottom = getScrollPosition() + 5 >= getScrollHeight();
      if (nearBottom) {
        loadNext(paginationBatchSize);
      }
    };

    const scrollHeight = getScrollHeight();
    const viewportHeight = window.innerHeight;

    if (scrollHeight <= viewportHeight) {
      if (!isLoadingNext) {
        loadNext(paginationBatchSize);
      }
    } else {
      window.addEventListener('scroll', handleScrollPagination);
      window.addEventListener('resize', handleScrollPagination);
      handleScrollPagination();
    }

    return () => {
      window.removeEventListener('scroll', handleScrollPagination);
      window.removeEventListener('resize', handleScrollPagination);
    };
  }, [data, hasNext, loadNext, isLoadingNext]);
};

export const useFocusOnInitialError = <
  T extends HTMLElement = HTMLInputElement,
>(
  errors: ErrorType[],
  refArg?: RefObject<T | null>
) => {
  const internalRef = useRef<T>(null);
  const ref = refArg ?? internalRef;
  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      if (errors.length > 0 && ref.current) {
        ref.current.focus();
      }
    }
  }, [errors, ref]);

  return ref;
};

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

export const useDebouncedState = <T>(
  initialValue: T,
  delay: number
): [T, (value: T) => void] => {
  const [value, setValue] = useState<T>(initialValue);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setDebouncedValue = (newValue: T) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setValue(newValue);
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [value, setDebouncedValue];
};
